import { useCallback, useMemo } from 'react'

import { Shift, ShiftHistory as ShiftHistoryApi } from '@api'
import { getCurrency } from '@common/utils/currency.ts'
import type { ColumnDef } from '@tanstack/react-table'
import { useNavigate } from 'react-router'

import { EmptyProvider } from '@/components/ui/empty/empty-provider'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content'
import { DataTable } from '@/components/ui/table/data-table'
import type { Creator } from '@/modules/creators/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { AppRoutes } from '@/utils/constants'
import { formatNumberWithCommas, getPercentValue } from '@/utils/helpers.ts'

import type { ShiftHistory, ShiftTracking } from '../types'
import { getUniqueShiftKey } from '../types'
import { ShiftView } from '../ui/shift'
import { ShiftsTableSkeleton } from '../ui/table-skeleton'
import { convertSecondsToMinutes, formatLocally } from '../utils'
import { getColorKey, getColorMap } from './utils'

interface Props {
  data?: ShiftHistoryApi[]
  isLoading: boolean
  filterShiftByPeople: (shift: Shift) => boolean
  creators: Creator[]
}

const trackingFieldsToShow = [
  'grossSoldRevenue',
  'sendMessages',
  'sendMediaMessages',
  'boughtMediaMessages',
  'ppvPurchaseRate',
  'averageResponseTime',
] as const

export const ShiftHistoryTable = ({
  data,
  isLoading,
  filterShiftByPeople,
  creators,
}: Props) => {
  const navigate = useNavigate()
  const shiftsHistory = data
    ?.filter(({ shift }) => shift && filterShiftByPeople(shift))
    .map(({ shift, tracking }) => ({
      shift: shift!,
      tracking: (tracking || []).reduce((acc, fields) => {
        trackingFieldsToShow.forEach((key) => {
          acc[key] = +((acc[key] ?? 0) + (fields[key] ?? 0)).toFixed(2)
        })

        return acc
      }, {} as ShiftTracking),
    }))
    .sort(
      (a, b) =>
        new Date(b.shift.startedAt).getTime() -
        new Date(a.shift.startedAt).getTime(),
    )

  const colorsMap = useMemo(() => {
    if (!shiftsHistory) return

    return getColorMap(shiftsHistory.map(({ shift }) => shift))
  }, [shiftsHistory])

  const columns: ColumnDef<ShiftHistory, number>[] = useMemo(
    () => [
      {
        accessorKey: 'shift.creatorId',
        header: 'Creator',
        cell: ({ getValue }) => {
          const creator = creators.find((c) => c.id === getValue())

          if (creator) {
            return (
              <div className="flex items-center space-x-2">
                <CreatorAvatar
                  key={creator?.id}
                  className="h-8 w-8 shrink-0"
                  name={creator.name}
                  avatarSrc={creator.avatar.croppedUrl}
                />
                <div className="text-sm">{creator.name}</div>
              </div>
            )
          }
        },
      },
      {
        accessorKey: 'shift.startedAt',
        header: 'Date',
        cell: ({ getValue }) =>
          formatLocally(new Date(getValue()), 'MMM dd, yyyy'),
      },
      {
        accessorKey: 'shift',
        header: 'Shift',
        cell: ({ getValue }) => {
          const shift = getValue<Shift>()

          const creator = creators.find((c) => c.id === shift.creatorId)

          if (creator && colorsMap) {
            return (
              <ShiftView
                color={colorsMap[getColorKey(shift)]}
                shift={shift}
                key={getUniqueShiftKey(shift)}
                creator={creator}
              />
            )
          }
        },
      },
      {
        accessorKey: 'tracking.grossSoldRevenue',
        header: 'Total Revenue',
        cell: ({ getValue }) => getCurrency(getValue() ?? 0),
      },
      {
        accessorKey: 'tracking.sendMessages',
        header: 'Sent Messages',
        cell: ({ getValue }) => formatNumberWithCommas(getValue() ?? 0, 0),
      },
      {
        accessorKey: 'tracking.sendMediaMessages',
        header: 'Sent PPVs',
        cell: ({ getValue }) => formatNumberWithCommas(getValue() ?? 0, 0),
      },
      {
        accessorKey: 'tracking.ppvPurchaseRate',
        header: 'PPV Purchase Rate',
        cell: ({ getValue }) => getPercentValue(getValue()),
      },
      {
        accessorKey: 'tracking.averageResponseTime',
        header: 'Avg. Response Time',
        cell: ({ getValue }) =>
          getValue() ? convertSecondsToMinutes(getValue<number>()) : '0s',
      },
    ],
    [creators, colorsMap],
  )

  const handleClickBackToShifts = () => {
    navigate(AppRoutes.Shifts.List.asUrl())
  }

  const renderEmpty = useCallback(
    () => (
      <EmptyTableContent>
        There are no shifts with the selected options. <br />
        Go back to the{' '}
        <span
          className="cursor-pointer text-primary-500"
          onClick={handleClickBackToShifts}
        >
          Shifts
        </span>{' '}
        page to create one.
      </EmptyTableContent>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  if (isLoading) {
    return <ShiftsTableSkeleton />
  }

  return (
    shiftsHistory && (
      <EmptyProvider render={renderEmpty}>
        <DataTable
          className="[&_table]:min-w-[950px] [&_table]:table-fixed [&_td]:px-2 [&_th]:px-2 [&_th]:pb-2"
          columns={columns}
          rowId={(row) => getUniqueShiftKey(row.shift)}
          enableSorting
          enablePagination
          data={shiftsHistory}
          paginationState={{ pageIndex: 0, pageSize: 10 }}
        />
      </EmptyProvider>
    )
  )
}
